// The fields that describe a worker to the load balancer.
//
// Shared by the edit dialog and the "Already running" branch of the Add worker
// wizard: registering an existing worker and editing a registered one ask for
// exactly the same things, and two copies of this form would drift the moment a
// field changed.

import type { FleetHostCreateInput } from "@schemas";
import {
	Field,
	FieldDescription,
	FieldError,
	FieldGroup,
	FieldLabel,
} from "@veil/ui/components/field";
import { Input } from "@veil/ui/components/input";
import {
	NativeSelect,
	NativeSelectOption,
} from "@veil/ui/components/native-select";
import { Switch } from "@veil/ui/components/switch";
import { Textarea } from "@veil/ui/components/textarea";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@veil/ui/components/tooltip";
import { cn } from "@veil/ui/lib/utils";
import Info from "lucide-react/dist/esm/icons/info";
import type { UseFormReturn } from "react-hook-form";

function LabelWithTooltip({
	htmlFor,
	label,
	tooltip,
	className,
}: {
	htmlFor: string;
	label: string;
	tooltip: string;
	className?: string;
}) {
	return (
		<FieldLabel
			htmlFor={htmlFor}
			className={cn("inline-flex items-center gap-1.5", className)}
		>
			{label}
			<Tooltip>
				<TooltipTrigger asChild>
					<button
						type="button"
						className="inline-flex rounded-full text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
						aria-label={`About ${label}`}
					>
						<Info className="size-3.5 shrink-0" />
					</button>
				</TooltipTrigger>
				<TooltipContent side="top" className="max-w-xs text-pretty">
					{tooltip}
				</TooltipContent>
			</Tooltip>
		</FieldLabel>
	);
}

export function WorkerConfigFields({
	form,
	/** True for a worker this manager deployed: its name is baked into the
	 *  container and into the id it mints its own URLs with. */
	nameIsFixed = false,
	/** Only remote workers need updater-agent wiring; same-box workers are
	 *  rolled directly over the docker socket. */
	showAgentFields = true,
	hasUpdaterAgent = false,
	/** What the worker reports about its location, shown as the auto value. */
	detectedLocation,
}: {
	form: UseFormReturn<FleetHostCreateInput>;
	nameIsFixed?: boolean;
	showAgentFields?: boolean;
	hasUpdaterAgent?: boolean;
	detectedLocation?: string;
}) {
	const errors = form.formState.errors;
	const enabled = form.watch("enabled");

	return (
		<FieldGroup className="gap-6">
			<div className="grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
				<Field data-invalid={!!errors.hostname}>
					<LabelWithTooltip
						htmlFor="hf-hostname"
						label="Hostname"
						tooltip="Short identifier for this worker (e.g. c1). It is the load-balancer route key: browsers on this worker are reached at /w/<hostname>/."
					/>
					<Input
						id="hf-hostname"
						className="h-9 px-3"
						placeholder="c1"
						readOnly={nameIsFixed}
						aria-readonly={nameIsFixed || undefined}
						aria-invalid={!!errors.hostname}
						{...form.register("hostname")}
					/>
					{/* The name of a worker we provisioned is baked into its
					    container and into the VEIL_WORKER_ID it mints its own
					    URLs with, neither of which a row edit can reach. */}
					{nameIsFixed ? (
						<FieldDescription>
							Fixed for workers deployed from here. Renaming the row would move
							the load balancer without moving the worker. Deploy under a new
							name and remove this one instead.
						</FieldDescription>
					) : null}
					<FieldError errors={[errors.hostname]} />
				</Field>
				<Field data-invalid={!!errors.publicIp}>
					<LabelWithTooltip
						htmlFor="hf-ip"
						label="Public IP"
						tooltip="Worker's public IPv4. Written into the nginx upstream block and added to the release-origin allow-list."
					/>
					<Input
						id="hf-ip"
						className="h-9 px-3"
						placeholder="5.161.75.243"
						aria-invalid={!!errors.publicIp}
						{...form.register("publicIp")}
					/>
					<FieldError errors={[errors.publicIp]} />
				</Field>
				<Field data-invalid={!!errors.lbPort}>
					<LabelWithTooltip
						htmlFor="hf-port"
						label="LB port (mlx/octo)"
						tooltip="Port the load-balancer proxies traffic to on this worker (mlx/octo session port)."
					/>
					<Input
						id="hf-port"
						className="h-9 px-3"
						type="number"
						aria-invalid={!!errors.lbPort}
						{...form.register("lbPort", { valueAsNumber: true })}
					/>
					<FieldError errors={[errors.lbPort]} />
				</Field>
				<Field data-invalid={!!errors.weight}>
					<LabelWithTooltip
						htmlFor="hf-weight"
						label="Weight"
						tooltip="Relative share of upstream traffic. A worker with weight 2 receives twice as many requests as one with weight 1."
					/>
					<Input
						id="hf-weight"
						className="h-9 px-3"
						type="number"
						aria-invalid={!!errors.weight}
						{...form.register("weight", { valueAsNumber: true })}
					/>
					<FieldError errors={[errors.weight]} />
				</Field>
				<Field data-invalid={!!errors.apiKey}>
					<LabelWithTooltip
						htmlFor="hf-api-key"
						label="API key"
						tooltip="X-API-Key sent when polling GET /fleet/status on this worker's local-api."
					/>
					<Input
						id="hf-api-key"
						className="h-9 px-3 font-mono text-sm"
						autoComplete="off"
						placeholder="(optional)"
						aria-invalid={!!errors.apiKey}
						{...form.register("apiKey")}
					/>
					<FieldError errors={[errors.apiKey]} />
				</Field>
				<Field data-invalid={!!errors.statusEndpoint}>
					<LabelWithTooltip
						htmlFor="hf-status-endpoint"
						label="Status endpoint"
						tooltip="Path appended to http://publicIp:lbPort for HTTP status polling. Default: /fleet/status."
					/>
					<Input
						id="hf-status-endpoint"
						className="h-9 px-3 font-mono text-sm"
						placeholder="/fleet/status"
						aria-invalid={!!errors.statusEndpoint}
						{...form.register("statusEndpoint")}
					/>
					<FieldError errors={[errors.statusEndpoint]} />
				</Field>
				<Field data-invalid={!!errors.basePath}>
					<LabelWithTooltip
						htmlFor="hf-base-path"
						label="Base path"
						tooltip="Optional URL path prefix on the LB in front of /fleet/* (e.g. /bb). Leave empty when the worker's local-api is on /. Applied to both polling and fleet-install."
					/>
					<Input
						id="hf-base-path"
						className="h-9 px-3 font-mono text-sm"
						placeholder="(empty)"
						aria-invalid={!!errors.basePath}
						{...form.register("basePath")}
					/>
					<FieldError errors={[errors.basePath]} />
				</Field>
				<Field data-invalid={!!errors.probePort}>
					<LabelWithTooltip
						htmlFor="hf-probe-port"
						label="Probe port"
						tooltip="TCP port used to test raw reachability when the HTTP poll fails. Default 22 (SSH). Helps distinguish 'host down' from 'API dead'."
					/>
					<Input
						id="hf-probe-port"
						className="h-9 px-3"
						type="number"
						aria-invalid={!!errors.probePort}
						{...form.register("probePort", { valueAsNumber: true })}
					/>
					<FieldError errors={[errors.probePort]} />
				</Field>
				<Field data-invalid={!!errors.region}>
					<LabelWithTooltip
						htmlFor="hf-region"
						label="Region"
						tooltip="Geo routing pool for /launch, e.g. de, fi or us-east. Leave empty to use the location the worker reports."
					/>
					<Input
						id="hf-region"
						className="h-9 px-3 font-mono text-sm"
						placeholder="auto"
						aria-invalid={!!errors.region}
						{...form.register("region", {
							setValueAs: (v) =>
								typeof v === "string" && v.trim() === ""
									? null
									: v.trim().toLowerCase(),
						})}
					/>
					{detectedLocation ? (
						<FieldDescription>{detectedLocation}</FieldDescription>
					) : null}
					<FieldError errors={[errors.region]} />
				</Field>
			</div>
			{showAgentFields ? (
				<div className="grid gap-4 sm:grid-cols-2">
					<Field data-invalid={!!errors.updaterAgentUrl}>
						<LabelWithTooltip
							htmlFor="hf-agent-url"
							label="Updater agent URL"
							tooltip="Where this host's updater-agent listens, e.g. http://10.0.0.5:3999. A container can't recreate itself, so rolling a remote worker to a new image goes through its agent. Leave empty to update that host by hand."
						/>
						<Input
							id="hf-agent-url"
							className="h-9 px-3 font-mono text-sm"
							placeholder="http://10.0.0.5:3999"
							aria-invalid={!!errors.updaterAgentUrl}
							{...form.register("updaterAgentUrl", {
								// An untouched text input reads as "", and the field is a
								// nullable URL — so without this, every edit of a worker with
								// no agent failed on "must be a URL" for a field the operator
								// never went near.
								setValueAs: (v) => (v === "" ? null : v),
							})}
						/>
						<FieldError errors={[errors.updaterAgentUrl]} />
					</Field>
					<Field data-invalid={!!errors.updaterAgentToken}>
						<LabelWithTooltip
							htmlFor="hf-agent-token"
							label="Updater agent token"
							tooltip="The AGENT_TOKEN you set on that host's updater-agent. Stored write-only; it is never sent back to the browser."
						/>
						<Input
							id="hf-agent-token"
							className="h-9 px-3 font-mono text-sm"
							type="password"
							autoComplete="off"
							placeholder={hasUpdaterAgent ? "(unchanged)" : "AGENT_TOKEN"}
							aria-invalid={!!errors.updaterAgentToken}
							{...form.register("updaterAgentToken")}
						/>
						<FieldError errors={[errors.updaterAgentToken]} />
					</Field>
				</div>
			) : null}
			<Field data-invalid={!!errors.notes}>
				<LabelWithTooltip
					htmlFor="hf-notes"
					label="Notes"
					tooltip="Free-text notes about this worker. Not used by any automation."
				/>
				<Textarea
					id="hf-notes"
					className="min-h-24 px-3 py-2.5"
					rows={3}
					aria-invalid={!!errors.notes}
					{...form.register("notes")}
				/>
				<FieldError errors={[errors.notes]} />
			</Field>
			{/* Editable because the column was backfilled from a heuristic — a
			    socket-managed container is on this box, anything else is not — which
			    was right for every row that existed at the time and is still a guess.
			    A worker registered by hand on the operator's own machine got called
			    remote, and only they can say otherwise. Kept out of the grid above
			    because it describes the machine rather than the route to it. */}
			<Field data-invalid={!!errors.placement}>
				<LabelWithTooltip
					htmlFor="hf-placement"
					label="Runs on"
					tooltip="Where this worker's container actually runs. Separate from whether this manager can control it: a remote worker may still be rollable through an updater agent or a saved SSH key."
				/>
				<NativeSelect
					id="hf-placement"
					className="h-9 max-w-xs px-3"
					aria-invalid={!!errors.placement}
					{...form.register("placement")}
				>
					<NativeSelectOption value="local">
						This machine (beside the manager)
					</NativeSelectOption>
					<NativeSelectOption value="remote">
						Another machine
					</NativeSelectOption>
				</NativeSelect>
				<FieldError errors={[errors.placement]} />
			</Field>
			<div className="flex items-center gap-3 rounded-lg border border-border/60 bg-muted/40 px-4 py-3.5 text-sm leading-snug">
				<Switch
					checked={enabled}
					onCheckedChange={(v) =>
						form.setValue("enabled", v, { shouldDirty: true })
					}
				/>
				<span>Enabled (included in upstream pool)</span>
			</div>
		</FieldGroup>
	);
}

/** Default values for a brand-new registration. */
export const NEW_WORKER_DEFAULTS: FleetHostCreateInput = {
	hostname: "",
	publicIp: "",
	lbPort: 38923,
	weight: 1,
	enabled: true,
	// Registering describes a worker this manager did not deploy, which is
	// almost always somewhere else. The field is right here to be corrected.
	placement: "remote",
	region: null,
	apiKey: "",
	statusEndpoint: "/fleet/status",
	basePath: "",
	probePort: 22,
	notes: "",
};

/** Normalise a filled form into the shape the register/patch routes accept. */
export function toWorkerPayload(values: FleetHostCreateInput) {
	const trimmedEndpoint = values.statusEndpoint?.trim() ?? "";
	const trimmedBasePath = values.basePath?.trim() ?? "";
	return {
		...values,
		statusEndpoint: trimmedEndpoint === "" ? "/fleet/status" : trimmedEndpoint,
		basePath: trimmedBasePath,
		apiKey:
			typeof values.apiKey === "string" && values.apiKey.trim() === ""
				? null
				: values.apiKey?.trim(),
		notes:
			typeof values.notes === "string" && values.notes.trim() === ""
				? null
				: values.notes,
	};
}
